/**
 * 🚀🎁 OFFRE DE BIENVENUE - VERSION DINGUERIE SPATIALE !
 * 
 * Mission: Capturer les emails des nouveaux voyageurs avec style !
 * Design: Cockpit de vaisseau spatial avec Pixie comme guide
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Mail, User, MapPin, Phone, Sparkles, Rocket, Star, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

// Avatar Pixie spatial (le bon !)
const PIXIE_AVATAR = 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png';

// 🔊 Son spatial au submit
const playSuccessSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    // Son de victoire spatiale
    osc.frequency.setValueAtTime(523, ctx.currentTime);
    osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
    osc.frequency.setValueAtTime(1047, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch (e) {
    console.log('Audio non supporté');
  }
};

// Particules d'étoiles animées
const StarParticles = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {[...Array(50)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 rounded-full bg-white"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 1, 0.2],
          scale: [0.5, 1.5, 0.5],
        }}
        transition={{
          duration: 2 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
  </div>
);

// Messages de Pixie - Messages d'accroche
const PIXIE_MESSAGES = [
  "Hey toi ! 🚀 Tu veux une offre de bienvenue qui déchire ?",
  "Psst... -10% sur ta première visite, ça te dit ? 🎁",
  "Bienvenue dans notre vaisseau ! Laisse-moi tes coordonnées... 😏",
  "Un code promo t'attend... Il suffit de remplir ce formulaire ! ✨"
];

// Messages de conseils de Pixie (quand on clique)
const PIXIE_ADVICE_MESSAGES = [
  "📝 Remplis soigneusement le formulaire pour recevoir ta superbe offre de -10% !",
  "📧 Et vérifie bien tes SPAMS après !",
  "💌 Ajoute-nous dans tes contacts pour ne rien rater ! À bientôt ! 👋"
];

export default function OffreBienvenuePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    prenom: '',
    email: '',
    codePostal: '',
    telephone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pixieMessage, setPixieMessage] = useState(0);
  const [showPixie, setShowPixie] = useState(true);
  const [pixieMode, setPixieMode] = useState('welcome'); // 'welcome' ou 'advice'
  const [adviceIndex, setAdviceIndex] = useState(0);

  // Changer le message de Pixie périodiquement (mode welcome)
  useEffect(() => {
    if (pixieMode === 'welcome') {
      const interval = setInterval(() => {
        setPixieMessage((prev) => (prev + 1) % PIXIE_MESSAGES.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [pixieMode]);

  // Gérer les messages de conseils (quand on clique)
  useEffect(() => {
    if (pixieMode === 'advice') {
      if (adviceIndex < PIXIE_ADVICE_MESSAGES.length - 1) {
        const timer = setTimeout(() => {
          setAdviceIndex((prev) => prev + 1);
        }, 3000); // 3 secondes par message
        return () => clearTimeout(timer);
      } else {
        // Dernier message affiché, on attend puis Pixie part
        const timer = setTimeout(() => {
          setShowPixie(false);
        }, 3500);
        return () => clearTimeout(timer);
      }
    }
  }, [pixieMode, adviceIndex]);

  // Fonction pour activer le mode conseils
  const handlePixieClick = () => {
    if (pixieMode === 'welcome') {
      setPixieMode('advice');
      setAdviceIndex(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.prenom || !formData.email) {
      toast.error('🚨 Prénom et email sont obligatoires !');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await api.createProspect(formData);
      playSuccessSound();
      toast.success('🎉 Mission accomplie ! Ton code arrive !');
      
      setTimeout(() => {
        navigate('/merci', { 
          state: { 
            email: formData.email, 
            prenom: formData.prenom,
            codePromo: result.prospect?.codePromo || null
          } 
        });
      }, 500);
    } catch (error) {
      toast.error('💥 Houston, on a un problème ! Réessaie.');
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-x-hidden overflow-y-auto"
      style={{ 
        background: 'radial-gradient(ellipse at center, #1a0a2e 0%, #0d0015 50%, #050008 100%)'
      }}
    >
      {/* Particules d'étoiles */}
      <StarParticles />

      {/* Cercles cosmiques de fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-[800px] h-[800px] rounded-full"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px solid rgba(255, 215, 0, 0.1)',
          }}
          animate={{ rotate: 360, scale: [1, 1.1, 1] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px solid rgba(0, 255, 255, 0.1)',
          }}
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      {/* Pixie flottante - Visible et majestueuse ! */}
      <AnimatePresence>
        {showPixie && (
          <motion.div
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            className="fixed left-6 bottom-24 z-50 max-w-xs hidden md:block"
          >
            <motion.div
              className="relative"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Bulle de message */}
              <motion.div
                key={pixieMode === 'welcome' ? pixieMessage : `advice-${adviceIndex}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`backdrop-blur-xl rounded-2xl p-4 mb-3 border ${
                  pixieMode === 'advice' 
                    ? 'bg-gradient-to-br from-green-900/90 to-emerald-900/90 border-green-500/30' 
                    : 'bg-gradient-to-br from-purple-900/90 to-pink-900/90 border-pink-500/30'
                }`}
                style={{
                  boxShadow: pixieMode === 'advice' 
                    ? '0 0 30px rgba(34, 197, 94, 0.4)' 
                    : '0 0 30px rgba(236, 72, 153, 0.3)'
                }}
              >
                <p className="text-white text-sm">
                  {pixieMode === 'welcome' 
                    ? PIXIE_MESSAGES[pixieMessage] 
                    : PIXIE_ADVICE_MESSAGES[adviceIndex]}
                </p>
                {/* Triangle pointer */}
                <div 
                  className={`absolute -bottom-2 left-12 w-4 h-4 rotate-45 ${
                    pixieMode === 'advice' ? 'bg-green-900/90' : 'bg-purple-900/90'
                  }`}
                  style={{ 
                    borderRight: pixieMode === 'advice' 
                      ? '1px solid rgba(34, 197, 94, 0.3)' 
                      : '1px solid rgba(236, 72, 153, 0.3)', 
                    borderBottom: pixieMode === 'advice' 
                      ? '1px solid rgba(34, 197, 94, 0.3)' 
                      : '1px solid rgba(236, 72, 153, 0.3)' 
                  }}
                />
                
                {/* Indicateur de progression en mode advice */}
                {pixieMode === 'advice' && (
                  <div className="flex justify-center gap-1 mt-2">
                    {PIXIE_ADVICE_MESSAGES.map((_, idx) => (
                      <div 
                        key={idx}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx <= adviceIndex ? 'bg-green-400' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
              
              {/* Avatar Pixie - Plus grand et visible ! */}
              <motion.div
                className={`w-28 h-28 rounded-full overflow-hidden border-4 cursor-pointer ${
                  pixieMode === 'advice' ? 'border-green-500/50' : 'border-pink-500/50'
                }`}
                style={{
                  boxShadow: pixieMode === 'advice'
                    ? '0 0 40px rgba(34, 197, 94, 0.6), 0 0 80px rgba(34, 197, 94, 0.3)'
                    : '0 0 40px rgba(236, 72, 153, 0.6), 0 0 80px rgba(236, 72, 153, 0.3)'
                }}
                whileHover={{ scale: 1.1 }}
                onClick={handlePixieClick}
              >
                <img src={PIXIE_AVATAR} alt="Pixie" className="w-full h-full object-cover" />
              </motion.div>
              
              {/* Badge - Change selon le mode */}
              <motion.div
                className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-white text-xs font-bold ${
                  pixieMode === 'advice' 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
                    : 'bg-gradient-to-r from-pink-600 to-purple-600'
                }`}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {pixieMode === 'advice' ? '💡 Conseils' : '✨ Pixie'}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contenu principal */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Badge Mission */}
        <motion.div 
          className="flex justify-center mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 
                          border border-cyan-500/30 backdrop-blur-sm">
            <span className="text-cyan-400 text-sm font-mono flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              MISSION: BIENVENUE VIP
            </span>
          </div>
        </motion.div>

        {/* Header avec Logo */}
        <div className="text-center mb-6">
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              filter: [
                'drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))',
                'drop-shadow(0 0 40px rgba(255, 215, 0, 0.8))',
                'drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))'
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <img 
              src={LOGO_URL} 
              alt="La Petite Épicerie" 
              className="h-24 md:h-32 mx-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </motion.div>
          
          {/* Titre avec effet néon */}
          <motion.h1 
            className="text-3xl md:text-4xl font-black text-transparent bg-clip-text 
                       bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 mb-2"
            style={{
              textShadow: '0 0 40px rgba(255, 215, 0, 0.5)'
            }}
          >
            🎁 OFFRE DE BIENVENUE
          </motion.h1>
          
          {/* Réduction animée */}
          <motion.div
            className="relative inline-block"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span 
              className="text-4xl md:text-5xl font-black text-transparent bg-clip-text 
                         bg-gradient-to-r from-green-400 to-emerald-400"
              style={{
                textShadow: '0 0 30px rgba(34, 197, 94, 0.8)'
              }}
            >
              -10%
            </span>
            <motion.div
              className="absolute -right-8 -top-2"
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </motion.div>
          </motion.div>
          <p className="text-gray-400 mt-2">sur ta 1ère visite en magasin !</p>
        </div>

        {/* Carte Formulaire - Style Cockpit */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="relative backdrop-blur-xl rounded-3xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(10, 20, 40, 0.95) 0%, rgba(5, 15, 35, 0.98) 100%)',
            border: '2px solid rgba(255, 215, 0, 0.4)',
            boxShadow: '0 0 60px rgba(255, 215, 0, 0.2), inset 0 1px 0 rgba(255, 215, 0, 0.1)'
          }}
        >
          {/* Effet scanlines */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 215, 0, 0.1) 2px, rgba(255, 215, 0, 0.1) 4px)',
            }}
          />
          
          {/* Coins HUD */}
          <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-yellow-500/60" />
          <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-yellow-500/60" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-yellow-500/60" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-yellow-500/60" />

          <div className="relative z-10 p-6">
            {/* Header du formulaire */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400/70 text-sm font-mono">TRANSMISSION EN COURS</span>
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Prénom */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2 text-sm mb-2">
                  <User className="w-4 h-4 text-yellow-400" />
                  Prénom <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Ton prénom de voyageur"
                  className="bg-black/50 border-yellow-500/30 text-white placeholder-gray-600
                             focus:border-yellow-400 focus:ring-yellow-400/20 transition-all"
                  required
                  data-testid="input-prenom"
                />
              </div>

              {/* Email */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2 text-sm mb-2">
                  <Mail className="w-4 h-4 text-yellow-400" />
                  Email <span className="text-red-400">*</span>
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ton@email.galactique"
                  className="bg-black/50 border-yellow-500/30 text-white placeholder-gray-600
                             focus:border-yellow-400 focus:ring-yellow-400/20 transition-all"
                  required
                  data-testid="input-email"
                />
              </div>

              {/* Code Postal */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2 text-sm mb-2">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  Code postal
                </Label>
                <Input
                  value={formData.codePostal}
                  onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
                  placeholder="62980"
                  className="bg-black/50 border-cyan-500/30 text-white placeholder-gray-600
                             focus:border-cyan-400 focus:ring-cyan-400/20 transition-all"
                  maxLength={5}
                  data-testid="input-codepostal"
                />
                <p className="text-xs text-cyan-500/60 mt-1 flex items-center gap-1">
                  <Rocket className="w-3 h-3" />
                  Pour savoir si notre navette peut te livrer !
                </p>
              </div>

              {/* Téléphone */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2 text-sm mb-2">
                  <Phone className="w-4 h-4 text-purple-400" />
                  Téléphone <span className="text-gray-600">(optionnel)</span>
                </Label>
                <Input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="06 XX XX XX XX"
                  className="bg-black/50 border-purple-500/30 text-white placeholder-gray-600
                             focus:border-purple-400 focus:ring-purple-400/20 transition-all"
                  data-testid="input-telephone"
                />
              </div>

              {/* Bouton Submit - Effet K2000 */}
              <motion.div
                className="relative mt-6 rounded-xl overflow-hidden"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Effet K2000 - Lumière qui défile */}
                <motion.div
                  className="absolute inset-0 z-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255, 215, 0, 0.4) 50%, transparent 100%)',
                    backgroundSize: '200% 100%',
                  }}
                  animate={{
                    backgroundPosition: ['200% 0%', '-200% 0%'],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
                
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="relative z-10 w-full py-5 text-lg font-black rounded-xl border-0"
                  style={{
                    background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 50%, #FFD700 100%)',
                    color: '#000',
                    boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
                  }}
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        🚀
                      </motion.span>
                      Transmission...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      RECEVOIR MON CODE -10%
                      <Sparkles className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </motion.div>
            </form>

            {/* Footer sécurité */}
            <motion.p 
              className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              🔒 Données cryptées - Jamais partagées
            </motion.p>
          </div>
        </motion.div>

        {/* Lien retour */}
        <motion.div 
          className="text-center mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <a 
            href="/"
            className="text-gray-500 hover:text-cyan-400 transition-colors text-sm inline-flex items-center gap-2"
          >
            ← Retour à la station orbitale
          </a>
        </motion.div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-gray-600 text-xs">
            La Petite Épicerie • 
            <span className="text-cyan-500/60 ml-1">la-petite-epicerie.shop</span>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
